/**
 * Deterministic stylesheet ordering for runtime-injected <style> tags.
 *
 * Problem
 * -------
 * `@lsq/nextgen` (and other libraries) ship each component's CSS as a runtime
 * `styleInject(...)` call that appends a <style> tag to <head> the first time
 * the component loads. In production the app's own CSS is extracted by
 * MiniCssExtractPlugin into <link> tags that already sit in <head>, so the
 * runtime-injected <style> lands AFTER them. On any equal-specificity tie the
 * later rule wins, so the injected library CSS beats our CSS — e.g. NextGen's
 * `.<unset_style> > * { all: unset }` (0,1,0) clobbering our `.chip` (0,1,0),
 * collapsing custom dropdown triggers.
 *
 * Worse, the winner differs between dev (style-loader injects app CSS as
 * <style> in import order) and prod (mini-css-extract emits <link>), so the
 * same code renders differently per environment.
 *
 * Fix
 * ---
 * Force every runtime-injected <style> to be inserted BEFORE the app's first
 * extracted stylesheet <link>. Our CSS then reliably wins equal-specificity
 * ties in every environment, without per-component specificity hacks.
 *
 * Notes
 * -----
 * - No-op in dev: there is no <link rel="stylesheet"> to anchor to, so injected
 *   <style> tags fall through to native appendChild and ordering is unchanged.
 * - Injection order among the moved <style> tags is preserved (each new one is
 *   inserted directly before the link, after previously-moved ones).
 * - Only <style> elements are relocated; <link> tags (incl. lazily code-split
 *   chunk CSS added by webpack) pass through untouched.
 * - Must be imported FIRST in the entry file, before any module that can
 *   trigger styleInject, so the patch is installed before the first injection.
 */
if (typeof document !== 'undefined' && document.head) {
  const head = document.head;
  const nativeAppendChild = head.appendChild.bind(head);
  const nativeInsertBefore = head.insertBefore.bind(head);

  head.appendChild = function appendChild(node) {
    if (node && node.nodeType === 1 && node.tagName === 'STYLE') {
      const firstStylesheetLink = head.querySelector('link[rel="stylesheet"]');
      if (firstStylesheetLink) {
        return nativeInsertBefore(node, firstStylesheetLink);
      }
    }
    return nativeAppendChild(node);
  };
}
